import zustandNoopStorage from "src/api/zustand/zustandNoopStorage";
import isWeb from "src/constants/isWeb";
import { HOUR_TO_MS } from "src/helpers/constants";
import API_PIN_DURATION from "src/shared/constants/API_PIN_DURATION";
import { getCurTime_MS } from "src/shared/helpers/timeHelpers";
import { Timestamp_MS } from "src/shared/types/general";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface MultiRegionStore {
	/**
	 * The time when the pin will be released (not including pin holds)
	 */
	pinEnd: Timestamp_MS;
	requestPin: (args?: {
		/**
		 * @default preferredPinDuration
		 */
		duration?: Timestamp_MS;
	}) => void;
	/**
	 * Current pin holds. These are ongoing requests that need to hold the pin.
	 */
	pinHolds: Set<Symbol>;
	/**
	 * Creates a new pin hold
	 */
	subscribePinHold: () => () => void;
	/**
	 * Checks if the pin is currently held
	 */
	checkIsPinned: () => boolean;
	preferredPinDuration: Timestamp_MS;
	setPreferredPinDuration: (duration: Timestamp_MS) => void;
}

const useMultiRegionStore = create<MultiRegionStore>()(
	persist(
		(set, get) => ({
			pinEnd: getCurTime_MS(),
			requestPin: ({ duration } = {}) => {
				// Ensures that pinEnd doesn't get locked in at some insane future distant date
				const { preferredPinDuration, pinEnd } = get();
				const newPinEnd =
					getCurTime_MS() + Math.min(duration ?? preferredPinDuration, HOUR_TO_MS);
				if (newPinEnd > pinEnd) {
					set({ pinEnd: newPinEnd });
				}
			},
			pinHolds: new Set(),
			subscribePinHold: () => {
				const id = Symbol();
				get().pinHolds.add(id);
				return () => {
					get().pinHolds.delete(id);
				};
			},
			checkIsPinned: () => {
				const { pinEnd, pinHolds: subscribers } = get();
				return subscribers.size > 0 || pinEnd > getCurTime_MS();
			},
			preferredPinDuration: API_PIN_DURATION,
			setPreferredPinDuration: (duration) => {
				if (duration < 0) return;
				if (duration === get().preferredPinDuration) return;
				set({ preferredPinDuration: duration });
			},
		}),
		{
			// Need to persist between web refreshes
			name: "multiRegionStore",
			storage: isWeb
				? createJSONStorage(() => localStorage)
				: createJSONStorage(() => zustandNoopStorage),
			partialize: (state) =>
				({
					pinEnd: state.pinEnd,
					preferredPinDuration: state.preferredPinDuration,
				}) satisfies Partial<MultiRegionStore>,
		},
	),
);
export default useMultiRegionStore;

export const requestApiPin: MultiRegionStore["requestPin"] = (args) => {
	return useMultiRegionStore.getState().requestPin(args);
};
